import { type MouseEvent, useState } from "react";
import { MailIcon, MessageSquareIcon } from "lucide-react";

import { QuoQuickAction } from "@jobs/shared/QuoQuickAction";
import { SendEmailDialog } from "@jobs/shared/SendEmailDialog";
import { Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { MinimalCandidacy, PipelineViewPreference } from "@/types";

export const CandidacyQuickActions = ({
  candidacy,
  onOpenMessages,
  pipelineView,
  className,
}: {
  candidacy: MinimalCandidacy;
  onOpenMessages: (candidacy: MinimalCandidacy) => void;
  pipelineView: PipelineViewPreference;
  className?: string;
}) => {
  const [isSendEmailOpen, setIsSendEmailOpen] = useState(false);

  const talentId = candidacy.talentId;
  const canSendEmail = candidacy.email && talentId !== null;

  /* Handlers */
  const handleOpenMessages = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onOpenMessages(candidacy);
  };

  const handleOpenSendEmail = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    mixpanelUtils.trackButtonClick("Send Email", MIXPANEL_SCREENS.JOB, {
      pipeline_view: pipelineView,
    });
    setIsSendEmailOpen(true);
  };

  /* Render */
  const render = () => (
    <TooltipProvider delayDuration={150}>
      <Row className={cn("items-center gap-1", className)}>
        <QuoQuickAction
          mixpanelMetadata={{ pipeline_view: pipelineView }}
          phone={candidacy.phone}
          quoConversationId={candidacy.quoConversationId}
          screenName={MIXPANEL_SCREENS.JOB}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="h-7 w-7 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              onClick={handleOpenMessages}
              size="icon"
              variant="ghost"
            >
              <MessageSquareIcon className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Open messages</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                className="h-7 w-7 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                disabled={!canSendEmail}
                onClick={handleOpenSendEmail}
                size="icon"
                variant="ghost"
              >
                <MailIcon className="h-3.5 w-3.5" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {canSendEmail
              ? "Send email"
              : "No email on file for this candidate"}
          </TooltipContent>
        </Tooltip>
      </Row>
      {canSendEmail && candidacy.email && (
        <Row
          className="contents"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <SendEmailDialog
            candidacyId={candidacy.id}
            onOpenChange={setIsSendEmailOpen}
            open={isSendEmailOpen}
            screenName={MIXPANEL_SCREENS.JOB}
            talentId={talentId}
            to={candidacy.email}
          />
        </Row>
      )}
    </TooltipProvider>
  );

  return render();
};
